import { type FileToUpload, useIntl } from '@capsicum/common';
import { DocumentPickerUpload } from '@capsicum/common-web';
import { CheckIcon } from '@capsicum/common-web/icons';
import { PROOF_OF_ADDRESS_UPLOAD_PROPS } from '@capsicum/features/regulations/shared';
import styled, { useTheme } from 'styled-components';

type Step2Props = {
	isLoading: boolean;
	onUploadFiles: (files: Array<FileToUpload>) => void;
};

/**
 * Postal Address Verification page
 *
 * @returns {JSX.Element} The Postal Address Verification page
 */

export const Step2 = ({ isLoading, onUploadFiles }: Step2Props) => {
	const { colors } = useTheme();
	const { $t } = useIntl();

	return (
		<S.StepContainer>
			<S.Title>{$t('postalAddressVerification.addDocuments')}</S.Title>
			<S.InstructionContent>
				<S.Instruction>
					<CheckIcon color={colors.backgrounds.main.inverse} />
					<S.InstructionText>{$t('postalAddressVerification.pleaseUploadADocument')}</S.InstructionText>
				</S.Instruction>
				<S.Instruction>
					<CheckIcon color={colors.backgrounds.main.inverse} />
					<S.InstructionText>{$t('postalAddressVerification.acceptations')}</S.InstructionText>
				</S.Instruction>
				<S.Instruction>
					<CheckIcon color={colors.backgrounds.main.inverse} />
					<S.InstructionText>{$t('postalAddressVerification.nameNeeded')}</S.InstructionText>
				</S.Instruction>
				<S.Instruction>
					<CheckIcon color={colors.backgrounds.main.inverse} />
					<S.InstructionText>{$t('postalAddressVerification.documentReadable')}</S.InstructionText>
				</S.Instruction>
			</S.InstructionContent>

			<div>
				<S.DocumentsListTitle>{$t('postalAddressVerification.documents')}</S.DocumentsListTitle>
				<S.DocumentsListText>{$t('postalAddressVerification.maxFilesNumber')}</S.DocumentsListText>
			</div>

			<DocumentPickerUpload
				isDisabled={isLoading}
				uploadProps={PROOF_OF_ADDRESS_UPLOAD_PROPS}
				onUploadedFiles={onUploadFiles}
			/>
		</S.StepContainer>
	);
};

namespace S {
	export const StepContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const Title = styled.h1`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: 0;
	`;

	export const InstructionContent = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const Instruction = styled.div`
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const InstructionText = styled.p`
		margin: 0;
		${({ theme }) => theme.fonts.text.text2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const DocumentsListTitle = styled.p`
		${({ theme }) => theme.fonts.text.text2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: 0;
	`;

	export const DocumentsListText = styled.p`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: 0;
	`;
}
